import { useState } from "react";
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Check,
  Star,
  Zap,
  Shield,
  FileText,
  ArrowRight,
  Phone,
  MessageCircle,
  Clock,
  Headphones,
  CheckCircle2,
  Sparkles,
  BadgeCheck,
  TrendingUp,
  Loader2,
  CreditCard,
  Building2,
  Wallet,
  Users,
  RotateCcw,
  ClipboardList,
  HandCoins,
  FolderOpen,
  BadgeEuro
} from "lucide-react";
import { Button } from "../components/ui/button";

const API = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const paymentMethods = [
  {
    id: "card",
    name: "Carte Bancaire",
    description: "Visa, Mastercard, CB",
    icon: CreditCard,
    color: "from-blue-500 to-indigo-600"
  },
  {
    id: "sepa",
    name: "Virement Bancaire",
    description: "SEPA - Délai 2-3 jours",
    icon: Building2,
    color: "from-emerald-500 to-teal-600"
  },
  {
    id: "paypal",
    name: "PayPal",
    description: "Paiement instantané",
    icon: Wallet,
    color: "from-blue-600 to-blue-800"
  }
];

export default function PricingPage() {
  const navigate = useNavigate();
  const [selectedPack, setSelectedPack] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState("card");
  const [loading, setLoading] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  const handleSelectPack = (packId) => {
    setSelectedPack(packId);
    setShowPaymentModal(true);
  };

  const handlePayment = async () => {
    setLoading(true);
    try {
      // Navigate to simulation or create checkout
      toast.success("Redirection vers le paiement...");
      navigate("/simulation");
    } catch (error) {
      toast.error("Erreur lors du paiement");
    } finally {
      setLoading(false);
      setShowPaymentModal(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden">
        {/* Background avec gradient vert écologique */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#064E3B] via-[#065F46] to-[#047857]">
          <div className="absolute inset-0 opacity-30">
            <div className="absolute top-20 left-10 w-72 h-72 bg-emerald-500 rounded-full filter blur-[120px]" />
            <div className="absolute bottom-20 right-10 w-96 h-96 bg-teal-400 rounded-full filter blur-[150px]" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-green-500 rounded-full filter blur-[200px] opacity-20" />
          </div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", delay: 0.2 }}
              className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-xl border border-white/20 rounded-full px-6 py-3 mb-6"
            >
              <div className="flex items-center justify-center w-10 h-10 bg-emerald-500 rounded-full animate-pulse">
                <Headphones size={20} className="text-white" />
              </div>
              <span className="text-white font-outfit font-bold text-lg">Nos Formules</span>
              <div className="flex -space-x-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={14} className="text-amber-400 fill-amber-400" />
                ))}
              </div>
            </motion.div>

            <h1 className="font-outfit font-bold text-4xl md:text-5xl lg:text-6xl text-white mb-6">
              Choisissez votre
              <span className="block mt-2 bg-gradient-to-r from-emerald-400 via-teal-300 to-cyan-400 bg-clip-text text-transparent">
                accompagnement
              </span>
            </h1>
            <p className="font-manrope text-white/70 text-lg max-w-2xl mx-auto">
              Des solutions adaptées à tous les projets de rénovation énergétique.
              Nos experts certifiés RGE vous accompagnent de A à Z.
            </p>
          </motion.div>

          {/* Support Features */}
          <div className="grid md:grid-cols-3 gap-6 mb-16">
            {[
              {
                icon: Phone,
                title: "Assistance téléphonique",
                desc: "Appelez-nous à tout moment pour une réponse immédiate",
                highlight: "Réponse < 2min"
              },
              {
                icon: MessageCircle,
                title: "Chat Expert en direct",
                desc: "Échangez avec nos conseillers via notre chatbot intelligent",
                highlight: "24h/24"
              },
              {
                icon: Clock,
                title: "Suivi personnalisé",
                desc: "Un conseiller dédié suit votre dossier de A à Z",
                highlight: "7j/7"
              }
            ].map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.15 }}
                className="group relative bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-8 hover:bg-white/10 hover:border-emerald-500/50 transition-all duration-500"
              >
                <div className="absolute -top-3 -right-3">
                  <span className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg">
                    {feature.highlight}
                  </span>
                </div>
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-500/20 to-teal-500/20 border border-emerald-500/30 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <feature.icon size={28} className="text-emerald-400" />
                </div>
                <h3 className="font-outfit font-bold text-xl text-white mb-3">{feature.title}</h3>
                <p className="font-manrope text-white/60 text-sm">{feature.desc}</p>
              </motion.div>
            ))}
          </div>

          {/* Packs Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-center mb-12"
          >
            <h2 className="font-outfit font-bold text-2xl md:text-3xl text-white mb-4">
              Nos formules d'accompagnement
            </h2>
          </motion.div>

          <div className="max-w-2xl mx-auto">
            {/* Pack unique Copilote eco+ */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ type: "spring", damping: 20, delay: 0.4 }}
              className="relative group"
            >
              {/* Badge */}
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-20">
                <motion.div
                  animate={{ y: [0, -5, 0] }}
                  transition={{ repeat: Infinity, duration: 2 }}
                  className="bg-gradient-to-r from-cyan-500 via-teal-500 to-emerald-500 text-white font-bold px-6 py-2 rounded-full text-sm shadow-lg shadow-teal-500/30 flex items-center gap-2"
                >
                  <Sparkles size={16} />
                  ACCOMPAGNEMENT EXPERT COMPLET
                </motion.div>
              </div>

              <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/30 to-emerald-500/30 rounded-[2rem] blur-xl group-hover:blur-2xl transition-all" />
              <div className="relative bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-xl border-2 border-cyan-400/50 rounded-[2rem] p-10 hover:border-cyan-400 transition-all flex flex-col">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <img
                      src="https://customer-assets.emergentagent.com/job_494a1d3d-3b3f-4afe-990a-a7037372e3a6/artifacts/ypve3c19_grok-image-2a6d0623-cb08-456d-9b3d-7d9f13cb557e.png.jpg"
                      alt="Copilote eco+"
                      className="w-14 h-14 rounded-xl object-cover flex-shrink-0"
                    />
                    <div>
                      <h3 className="font-outfit font-bold text-2xl text-white">Pack Copilote eco+</h3>
                      <p className="text-cyan-400 text-sm font-manrope">Un accompagnement d'experts à vos côtés</p>
                    </div>
                  </div>
                  <BadgeCheck size={36} className="text-cyan-400" />
                </div>

                <div className="flex items-baseline gap-2 mb-8">
                  <span className="font-syne text-6xl font-bold text-white">19,90€</span>
                  <span className="text-white/50 text-sm">paiement unique et sécurisé</span>
                </div>

                <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3 mb-6">
                  {[
                    "Devis illimités et précis pour vos travaux prioritaires",
                    "Analyses énergétiques complètes + Rapport PDF personnalisé",
                    "Recommandations classées par priorité et budget",
                    "Comparateur d'artisans RGE certifiés + suivi des réponses",
                    "Simulation complète des aides financières mobilisables",
                    "Checklists personnalisées et modèles de mails prêts à l'emploi",
                    "Accompagnement d'experts à vos côtés tout au long du projet",
                    "Support prioritaire 7j/7",
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-white font-manrope text-sm list-none">
                      <CheckCircle2 size={16} className="text-cyan-400 flex-shrink-0 mt-0.5" />
                      {item}
                    </li>
                  ))}
                </div>

                <p className="text-cyan-300/80 italic text-sm mb-8 border-l-2 border-cyan-400/40 pl-4">
                  "Avec un accompagnement d'experts à vos côtés, vos travaux de rénovation deviennent simples, rentables et sans stress."
                </p>

                <Button 
                  onClick={() => handleSelectPack("copilote-eco-plus")}
                  className="w-full bg-gradient-to-r from-cyan-400 via-teal-400 to-emerald-400 text-[#0F172A] rounded-xl py-6 font-outfit font-bold text-lg hover:opacity-90 transition-opacity group shadow-lg shadow-cyan-500/30"
                  data-testid="pack-copilote-eco-plus-btn"
                >
                  Choisir Copilote eco+ — 19,90€
                  <TrendingUp size={20} className="ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>

                <div className="flex items-center justify-center gap-4 mt-4 text-white/40 text-xs">
                  <span className="flex items-center gap-1"><Shield size={12} /> Paiement sécurisé</span>
                  <span>*hors déplacement</span>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Trust Badges */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="flex flex-wrap justify-center items-center gap-8 mt-16 pt-12 border-t border-white/10"
          >
            {[
              { icon: Shield, text: "Paiement sécurisé" },
              { icon: BadgeCheck, text: "Experts certifiés RGE" },
              { icon: Clock, text: "Support 7j/7" },
              { icon: Star, text: "98% clients satisfaits" }
            ].map((badge, index) => (
              <div key={index} className="flex items-center gap-2 text-white/60">
                <badge.icon size={18} className="text-emerald-400" />
                <span className="font-manrope text-sm">{badge.text}</span>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Services Additionnels Section */}
      <section className="py-24 bg-gradient-to-b from-[#047857] to-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-outfit font-bold text-3xl md:text-4xl text-white mb-4">
              Services complémentaires
            </h2>
            <p className="font-manrope text-white/70 max-w-2xl mx-auto">
              Des prestations professionnelles pour compléter votre projet
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Diagnostics immobiliers obligatoires */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-8 shadow-xl border border-gray-100"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 rounded-xl bg-emerald-100 flex items-center justify-center">
                  <FileText className="text-emerald-600" size={28} />
                </div>
                <div>
                  <h3 className="font-outfit font-bold text-xl text-gray-900">Diagnostics immobiliers obligatoires</h3>
                  <p className="text-emerald-600 font-medium">Par expert certifié</p>
                </div>
              </div>
              
              <div className="flex items-baseline gap-2 mb-6">
                <span className="font-syne text-4xl font-bold text-gray-900">À partir de 190€</span>
                <span className="text-gray-500 text-sm">TTC</span>
              </div>

              <ul className="space-y-3 mb-6">
                {["DPE obligatoire pour vente/location", "Rapport détaillé et complet", "Valable 10 ans", "Expert certifié COFRAC"].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-gray-600 text-sm">
                    <Check className="text-emerald-500" size={16} />
                    {item}
                  </li>
                ))}
              </ul>

              <Link to="/simulation">
                <Button className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl py-3">
                  Demander un devis
                </Button>
              </Link>
            </motion.div>

            {/* Audit Énergétique */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-2xl p-8 shadow-xl border border-gray-100"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 rounded-xl bg-teal-100 flex items-center justify-center">
                  <Zap className="text-teal-600" size={28} />
                </div>
                <div>
                  <h3 className="font-outfit font-bold text-xl text-gray-900">Audit Énergétique</h3>
                  <p className="text-teal-600 font-medium">Analyse complète</p>
                </div>
              </div>
              
              <div className="flex items-baseline gap-2 mb-6">
                <span className="font-syne text-4xl font-bold text-gray-900">290€</span>
                <span className="text-gray-500 text-sm">TTC</span>
              </div>

              <ul className="space-y-3 mb-6">
                {["Analyse thermique complète", "Scénarios de travaux détaillés", "Estimation des aides", "Plan d'action personnalisé"].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-gray-600 text-sm">
                    <Check className="text-teal-500" size={16} />
                    {item}
                  </li>
                ))}
              </ul>

              <Link to="/simulation">
                <Button className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-xl py-3">
                  Demander un devis
                </Button>
              </Link>
            </motion.div>
          </div>

          {/* Pack Mon Accompagnateur Rénove - Pleine largeur */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="max-w-4xl mx-auto mt-8"
          >
            <div className="relative bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
              {/* Badge remboursé */}
              <div className="absolute top-6 right-6 z-10">
                <span className="inline-flex items-center gap-1.5 bg-[#1a2b6d] text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-md">
                  <RotateCcw size={12} />
                  499€ REMBOURSÉS*
                </span>
              </div>

              {/* Bandeau titre */}
              <div className="bg-gradient-to-r from-[#1a2b6d] to-[#2d3f9e] px-8 py-5 flex items-center gap-4">
                <img
                  src="https://customer-assets.emergentagent.com/job_494a1d3d-3b3f-4afe-990a-a7037372e3a6/artifacts/3eesj9ra_IMG_0008.jpeg"
                  alt="Mon Accompagnateur Renov France Renov"
                  className="w-14 h-14 rounded-full object-cover bg-white p-0.5 shadow-md flex-shrink-0"
                />
                <div>
                  <h3 className="font-outfit font-bold text-xl text-white">
                    Pack « Mon accompagnateur rénove »
                  </h3>
                  <p className="text-blue-200 font-medium text-sm">Accompagnement personnalisé de A à Z</p>
                </div>
              </div>

              <div className="relative p-8">
                {/* Prix */}
                <div className="flex items-baseline gap-3 mb-2">
                  <span className="font-syne text-5xl font-bold text-gray-900">499€</span>
                  <div>
                    <span className="block text-gray-500 text-sm">TTC</span>
                    <span className="block text-emerald-600 font-semibold text-sm">remboursés si éligible*</span>
                  </div>
                </div>

                <p className="text-gray-600 text-sm mb-6 max-w-2xl leading-relaxed">
                  Confiez votre projet de rénovation à un accompagnateur dédié qui vous aide à définir les bons travaux,
                  à mobiliser les aides financières et à monter votre dossier de A à Z.
                  Le pack est facturé 499€, puis <strong>intégralement remboursé</strong> par le dispositif d'aide, si vous êtes éligible.
                </p>

                {/* Contenu du pack — grille */}
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
                  {[
                    { icon: ClipboardList, label: "Analyse de votre logement", desc: "Échange, collecte d'infos, étude de votre situation" },
                    { icon: TrendingUp, label: "Recommandations de travaux", desc: "Priorités, gains énergétiques, budget indicatif" },
                    { icon: BadgeEuro, label: "Vérification des aides", desc: "État, collectivités, autres dispositifs" },
                    { icon: FolderOpen, label: "Montage du dossier d'aides", desc: "Pièces à fournir, formulaires, dépôt" },
                    { icon: HandCoins, label: "Suivi administratif", desc: "Jusqu'à l'acceptation de votre dossier d'aide" },
                    { icon: Shield, label: "Accompagnement dédié", desc: "Un interlocuteur unique tout au long du projet" },
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3 bg-gray-50 rounded-xl p-4 border border-gray-100 hover:border-blue-200 hover:bg-blue-50/30 transition-all">
                      <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <item.icon className="text-[#1a2b6d]" size={16} />
                      </div>
                      <div>
                        <p className="text-gray-900 font-semibold text-sm">{item.label}</p>
                        <p className="text-gray-500 text-xs mt-0.5 leading-snug">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* CTA + mention légale */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                  <Link to="/simulation" className="flex-shrink-0">
                    <Button className="bg-[#1a2b6d] hover:bg-[#2d3f9e] text-white px-8 py-3 rounded-xl font-semibold text-base shadow-md shadow-blue-200 transition-all hover:shadow-lg">
                      Demander cet accompagnement
                      <ArrowRight size={18} className="ml-2" />
                    </Button>
                  </Link>
                  <p className="text-gray-400 text-xs leading-snug max-w-sm">
                    * Selon les conditions du dispositif d'aide en vigueur. L'éligibilité est vérifiée lors de l'analyse de votre situation.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-outfit font-bold text-3xl text-gray-900 mb-4">
              Une question sur nos offres ?
            </h2>
            <p className="text-gray-600 mb-8">
              Notre équipe est disponible pour vous conseiller et répondre à toutes vos questions.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/simulation">
                <Button className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-3 rounded-xl">
                  Démarrer ma simulation gratuite
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="border-emerald-600 text-emerald-600 hover:bg-emerald-50 px-8 py-3 rounded-xl">
                  Nous contacter
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl p-8 max-w-md w-full shadow-2xl"
          >
            <h3 className="font-outfit font-bold text-2xl text-gray-900 mb-2">
              Finaliser votre achat
            </h3>
            <p className="text-gray-600 mb-6">
              Pack Copilote eco+ — 19,90€ (paiement unique)
            </p>

            <div className="space-y-3 mb-6">
              {paymentMethods.map((method) => (
                <button
                  key={method.id}
                  onClick={() => setSelectedPayment(method.id)}
                  className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 transition-all ${
                    selectedPayment === method.id
                      ? "border-emerald-500 bg-emerald-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${method.color} flex items-center justify-center`}>
                    <method.icon className="text-white" size={20} />
                  </div>
                  <div className="text-left">
                    <p className="font-medium text-gray-900">{method.name}</p>
                    <p className="text-sm text-gray-500">{method.description}</p>
                  </div>
                  {selectedPayment === method.id && (
                    <Check className="ml-auto text-emerald-500" size={20} />
                  )}
                </button>
              ))}
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowPaymentModal(false)}
                className="flex-1"
              >
                Annuler
              </Button>
              <Button
                onClick={handlePayment}
                disabled={loading}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                {loading ? (
                  <Loader2 className="animate-spin" size={20} />
                ) : (
                  "Payer maintenant"
                )}
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
